import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import {
  AddOnsPromptButton,
  AddOnsPromptButtonContainer,
  AddOnsPromptContainer,
  AddOnsPromptDescription,
  AddOnsPromptTitle,
} from './styles';

interface AddOnsPromptProps {
  /*
   * The id to hand the step, decided by the CALLER.
   *
   * This component used to call `useBookingAddOnsOffer` itself, and once it moved inside the screen
   * that hook's answer renders, that was a feedback loop: a second instance dispatched the same menu
   * read, the store went PENDING, the parent flipped back to its hold, this unmounted, the read
   * settled, and round again — a visibly flashing screen. One decision, one owner.
   */
  bookingId: string;
  /*
   * Navigation is injected rather than built here, and that is the guard: `receiptPage.tsx` renders
   * Receipt outside any PaymentContent, where `pathname.replace('receipt', …)` yields a route that
   * matches nothing. No navigator, no card.
   */
  onBrowseAddOns: (bookingId: string) => void;
}

/* The door to the post-booking add-on step. Presentational: the caller has already decided. */
export const AddOnsPrompt = ({ bookingId, onBrowseAddOns }: AddOnsPromptProps): JSX.Element => {
  const intl = useIntl();

  const onClick = useCallback(() => onBrowseAddOns(bookingId), [onBrowseAddOns, bookingId]);

  /* No section heading: it repeated the card's own title and cost 54px of a 124px budget. */
  return (
    <AddOnsPromptContainer data-testid="add-ons-prompt">
      <AddOnsPromptTitle data-testid="add-ons-prompt-title">
        {intl.formatMessage({ id: 'add_on_prompt_title' })}
      </AddOnsPromptTitle>
      <AddOnsPromptDescription data-testid="add-ons-prompt-description">
        {intl.formatMessage({ id: 'add_on_prompt_body' })}
      </AddOnsPromptDescription>
      <AddOnsPromptButtonContainer>
        <AddOnsPromptButton
          data-testid="add-ons-prompt-cta"
          text={intl.formatMessage({ id: 'add_on_prompt_cta' })}
          variant="primary"
          onClick={onClick}
        />
      </AddOnsPromptButtonContainer>
    </AddOnsPromptContainer>
  );
};
